<template>
  <div style="position: relative; width: 100%; height: 100vh; overflow: hidden; margin-top: 1%; max-height: 950px;">
    <div id="mapView" style="width: 100%; height: 100%;" v-loading="loading" element-loading-text="LOADING..."></div>
    
    <div class="controls-wrapper">
      <div id="basemapMenu" class="control-panel" :class="{ 'menu-collapsed': !isMenuOpen }">
        <div class="panel-header" @click="isMenuOpen = !isMenuOpen">
          <span class="panel-title">{{ $t('map2.layer_control') }}</span>
          <span class="toggle-icon">{{ isMenuOpen ? '▼' : '▲' }}</span>
        </div>

        <div class="panel-content" v-show="isMenuOpen">
          
          <div class="menu-group">
            <div class="group-title">{{ $t('map2.basemap') }}</div>
            <div class="group-items">
              <div 
                v-for="item in filteredBasemapList" 
                :key="item.id" 
                class="menu-item"
                :class="{ 'item-selected': currentBasemapId === item.id }"
                @click="changeBasemap(item)"
              >
                <span class="item-text">{{ item.name }}</span>
                <span v-if="currentBasemapId === item.id" class="check-mark">✔</span>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="menu-group" v-if="filteredOverlayList.length > 0">
            <div class="group-title">{{ $t('map2.weather') }}</div>
            <div class="group-items">
              <div v-for="item in filteredOverlayList" :key="item.id" class="overlay-container">
                <div 
                  class="menu-item overlay-parent" 
                  :class="{ 'item-checked': item.visible }"
                  @click="toggleLayer(item)"
                >
                  <div class="checkbox-wrapper">
                    <input type="checkbox" :checked="item.visible">
                  </div>
                  <span class="item-text">{{ item.name }}</span>
                </div>

                <transition name="slide-fade">
                  <div v-if="item.children && item.visible" class="child-layer-container">
                    <div 
                      v-for="child in item.children" 
                      :key="child.id"
                      class="child-item"
                      :class="{ 'child-selected': item.activeChildId === child.id }"
                      @click.stop="switchChildLayer(item, child)"
                    >
                      <div class="radio-wrapper">
                        <span class="radio-outer">
                          <span class="radio-inner" v-if="item.activeChildId === child.id"></span>
                        </span>
                      </div>
                      <span class="child-text">{{ child.name }}</span>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <div class="menu-group" v-if="filteredAuxiliaryList.length > 0">
            <div class="group-title">{{ $t('map2.auxiliary') }}</div>
            <div class="group-items">
              <div v-for="item in filteredAuxiliaryList" :key="item.id" class="overlay-container">
                
                <div 
                  class="menu-item overlay-parent" 
                  :class="{ 'item-checked': item.visible }"
                  @click="toggleLayer(item)"
                >
                  <div class="checkbox-wrapper">
                    <input type="checkbox" :checked="item.visible">
                  </div>
                  <span class="item-text">{{ item.name }}</span>
                </div>

                <transition name="slide-fade">
                  <div v-if="item.children && item.visible" class="child-layer-container">
                    <div 
                      v-for="child in item.children" 
                      :key="child.id"
                      class="child-item"
                      :class="{ 'child-selected': item.activeChildId === child.id }"
                      @click.stop="switchChildLayer(item, child)"
                    >
                      <div class="radio-wrapper">
                        <span class="radio-outer">
                          <span class="radio-inner" v-if="item.activeChildId === child.id"></span>
                        </span>
                      </div>
                      <span class="child-text">{{ child.name }}</span>
                    </div>
                  </div>
                </transition>
                </div>
            </div>
          </div>

          <div class="menu-group" v-if="filteredTraList.length > 0">
          <div class="divider"></div>
          <div class="menu-group">
            <div class="group-title">{{ $t('map2.TRA') }}</div>
            <div class="group-items">
              <div v-for="item in filteredTraList" :key="item.id" class="overlay-container">
                <div class="menu-item overlay-parent tra-header">
                  
                  <div class="checkbox-wrapper" @click.stop="toggleAllTraGroup(item)">
                    <input type="checkbox" 
                          :checked="isTraGroupChecked(item)"
                          :indeterminate.prop="isTraGroupIndeterminate(item)">
                  </div>

                  <div class="item-content-clickable" @click="toggleTraExpansion(item)">
                    <span class="item-text">{{ item.name }}</span>
                    <span class="expand-arrow" :class="{ 'is-expanded': item.expanded }">▼</span>
                  </div>
                </div>

                <transition name="slide-fade">
                  <div v-if="item.expanded" class="child-layer-container scrollable-list">
                    <div 
                      v-for="child in item.children" 
                      :key="child.id"
                      class="child-item"
                      :class="{ 'child-selected': child.visible }"
                      @click.stop="toggleTraChild(item, child)"
                    >
                      <div class="checkbox-wrapper child-check">
                        <input type="checkbox" :checked="child.visible">
                      </div>
                      <span class="child-text">{{ child.name }}</span>
                      <span class="color-legend" :style="{ backgroundColor: child.color }"></span>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      <div id="instrumentMenu" class="control-panel" :class="{ 'menu-collapsed': !isInstrumentMenuOpen }" v-if="showInstrumentMenu">
        <div class="panel-header" @click="isInstrumentMenuOpen = !isInstrumentMenuOpen">
          <span class="panel-title">{{ $t('map2.instrument_control') }}</span>
          <span class="toggle-icon">{{ isInstrumentMenuOpen ? '▼' : '▲' }}</span>
        </div>

        <div class="panel-content" v-show="isInstrumentMenuOpen">
          <div class="menu-group">
            <div class="group-title">{{ $t('map2.filter') }}</div>
            <div class="group-items">
              <div class="menu-item" @click="toggleAllInstruments">
                 <div class="checkbox-wrapper">
                  <input type="checkbox" :checked="isAllInstrumentsChecked" @click.stop="toggleAllInstruments">
                </div>
                <span class="item-text" style="font-weight:bold;">{{ $t('map2.select_all') }} / {{ $t('map2.deselect_all') }}</span>
              </div>

              <div 
                v-for="(typeObj, index) in instrumentTypes" 
                :key="index" 
                class="menu-item"
                :class="{ 'item-checked': typeObj.visible }"
                @click="toggleInstrumentType(typeObj)"
              >
                <div class="checkbox-wrapper">
                  <input type="checkbox" :checked="typeObj.visible">
                </div>
                <span class="item-text">{{ typeObj.name }}</span>
                <span class="count-badge">{{ typeObj.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div id="statusWidget" class="status-widget-container" v-if="badInstrumentStatusList.length > 0">
      
      <transition name="slide-up">
        <div class="status-list-panel" v-show="isStatusMenuOpen">
          <div class="status-header">
            {{ $t('map2.instrument_list') }} ({{ badInstrumentStatusList.length }})
          </div>
          <div class="status-content">
            <div 
              v-for="(inst, index) in badInstrumentStatusList" 
              :key="index" 
              class="status-row"
              @click="flyToInstrument(inst)"
            >
              <span class="status-indicator" :style="{ backgroundColor: getStatusColor(inst.status) }"></span>
              <span class="inst-name">{{ inst.name }}</span>
              <span class="inst-tag" :style="{ color: getStatusColor(inst.status), borderColor: getStatusColor(inst.status) }">
                {{ inst.status }}
              </span>
            </div>
          </div>
        </div>
      </transition>

      <div class="status-pill-btn" @click="isStatusMenuOpen = !isStatusMenuOpen" :class="{ 'is-active': isStatusMenuOpen }">
        <span class="pill-text">{{ $t('map2.instrument_status') }}</span>
        <span class="pill-badge" v-if="badInstrumentStatusList.length > 0">{{ badInstrumentStatusList.length }}</span>
      </div>

    </div>

  </div>
</template>

<script>
import JSZip from 'jszip';
import { loadModules } from 'esri-loader';
import { isThsrProject } from '@/lib/base/project';
import { i18n } from '@/i18n/index.js';
import { geojsonToArcGIS } from '@esri/arcgis-to-geojson-utils';
import shp from 'shpjs/dist/shp';
import moment from 'moment';

let BasemapClass = null;
let WebTileLayerClass = null;
let MediaLayerClass = null;
let ImageElementClass = null;
let ExtentClass = null;
let KMLLayerClass = null;
let GraphicsLayerClass = null;
let GraphicClass = null;
let SpatialReferenceClass = null;

export default {
  name: 'ArcGISMap',
  data() {
    return {
      isMenuOpen: true,
      // NEW: 儀器選單控制
      isInstrumentMenuOpen: true,
      // NEW: 存放解析出的儀器類型清單 { name: 'ELP', visible: true, count: 0 }
      instrumentTypes: [],
      loading: true,
      layer: null,
      view: null,
      isStatusMenuOpen: false, // 預設不展開
      rawInstrumentList: [],   // 用來儲存從 API 拉回來的原始儀器資料
      // **新增：用於儲存 WebMap 的 ID，以便在切換時重複使用**
      webmapId: '0bec10c19e494061b1358e4b83e586a5', // 高鐵專用 WebMap ID
      // **新增：追蹤是否成功載入 WebMap**
      isWebMapMode: false,
      // ⭐ 新增：用來儲存 WebMap 剛載入時的原始底圖物件
      originalWebMapBasemap: null,
      currentBasemapId: 'topo-vector', // 當前選中的底圖 ID
      // 存放已建立的圖層實體，避免重複建立 Key: id, Value: LayerObject
      createdLayers: {},
      // 哪些圖層要在選單開啟
      allowLayer: {
        echo: 'echo',
        typhon: 'typhon',
        rainfall: 'rain_group',
        contour: 'MOI_CONTOUR_2',
        earthquake: 'shakemap',
        shp: 'shp_group',
        train: 'train_layer',
      },
      // 底圖清單配置
      basemapList: [
        {
          // 將預設地圖 ID 改為一個特殊值，以便在 mounted 時判斷是否替換為 WebMap ID
          id: 'initial-default-map', // 暫時的預設 ID
          name: '預設地圖',
          type: 'default',
          showInMenu: true,
        },
        {
          id: 'PHOTO_MIX',
          name: i18n.t('map.orthoimagery_mix') || '正射影像(混合)', 
          url: 'https://wmts.nlsc.gov.tw/wmts/PHOTO_MIX/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}',
          type: 'wmts',
          showInMenu: true,
        },
        {
          id: 'EMAP',
          name: i18n.t('map.general_electronic_map') || '通用電子地圖',
          url: 'https://wmts.nlsc.gov.tw/wmts/EMAP/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}',
          type: 'wmts',
          showInMenu: true,
        },
        {
          id: 'PHOTO2',
          name: i18n.t('map.orthoimagery_general') || '正射影像',
          url: 'https://wmts.nlsc.gov.tw/wmts/PHOTO2/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}',
          type: 'wmts',
          showInMenu: true,
        },
      ],
      traList: [
        {
          id: 'train_layer',
          name: '台鐵圖層',
          type: 'train_layer',
          showInMenu: false, // 由 getProject 控制
          visible: true,     // 整體大開關
          expanded: true,
          children: [
            { id: 'tra_1', name: '宜蘭線-北迴線', color: '#2b7bff', url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-宜蘭線-路線座標(K000+000~K94+000) -北迴線-路線座標(K000+000~K79+400)(ORI).kml', visible: true, zIndex: 20 },
            { id: 'tra_2', name: '平溪線及深澳線', color: '#E75000', url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-平溪線及深澳線(ORI).kml', visible: true, zIndex: 20 },
            { id: 'tra_3', name: '北工段百公尺標', color: '#409EFF', url: 'https://api-geoserver.geosupply.com.tw/kmz/北工段百公尺標(八堵)_1.kmz', visible: true, zIndex: 20 },
            { id: 'tra_4', name: '北迴線', color: '#66CC00', url: 'https://api-geoserver.geosupply.com.tw/kmz/北迴線.kml', visible: true, zIndex: 30 },
            { id: 'tra_5', name: '宜蘭線', color: '#D4C21D', url: 'https://api-geoserver.geosupply.com.tw/kmz/宜蘭線.kml', visible: true, zIndex: 30 },
            { id: 'tra_6', name: '深澳線', color: '#FF4FA8', url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-深澳線.kml', visible: true, zIndex: 30 },
            { id: 'tra_7', name: '平溪線', color: '#8C3BFF', url: 'https://api-geoserver.geosupply.com.tw/kmz/GIS-平溪線.kml', visible: true, zIndex: 30 },
            { id: 'tra_8', name: '北工段百公尺標線', color: '#008B8B', url: 'https://api-geoserver.geosupply.com.tw/kmz/北工段百公尺標線.kml', visible: true, zIndex: 30 },
          ],
        },
      ],
      overlayList: [
        {
          id: 'echo',
          name: '雷達回波圖',
          type: 'media',
          showInMenu: false,
          url: 'https://cwaopendata.s3.ap-northeast-1.amazonaws.com/Observation/O-A0058-005.png',
          visible: false, // 控制 UI 顯示狀態
          opacity: 0.5,   // 設定透明度
          extent: {
            xmin: 115.00, ymin: 17.75, xmax: 126.5, ymax: 29.25,
          },
        },
        {
          id: 'typhon',
          name: i18n.t('map.typhon') || '颱風路徑圖',
          type: 'kml',
          showInMenu: false,
          url: 'https://alerts.ncdr.nat.gov.tw/DownLoadNewAssistData.ashx/1',
          visible: false,
        },
        {
          id: 'rain_group', // 群組 ID
          name: '累積雨量圖',
          type: 'media_group',
          showInMenu: false,
          visible: false,   // 父層開關
          opacity: 0.8,
          activeChildId: 'rain_24h', // 預設選中的子項目 ID
          
          // 子圖層定義 (假設範圍 Extent 都相同)
          children: [
            {
              id: 'rain_3h',
              name: '累積雨量圖 (3H)',
              type: 'media',
              url: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/HOUR_3.png',
              extent: { xmin: 119.77, ymin: 21.746776427875963, xmax: 122.67, ymax: 25.449655229327627 },
            },
            {
              id: 'rain_6h',
              name: '累積雨量圖 (6H)',
              type: 'media',
              url: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/HOUR_6.png',
              extent: { xmin: 119.77, ymin: 21.746776427875963, xmax: 122.67, ymax: 25.449655229327627 },
            },
            {
              id: 'rain_12h',
              name: '累積雨量圖 (12H)',
              type: 'media',
              url: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/HOUR_12.png',
              extent: { xmin: 119.77, ymin: 21.746776427875963, xmax: 122.67, ymax: 25.449655229327627 },
            },
            {
              id: 'rain_24h',
              name: '累積雨量圖 (24H)',
              type: 'media',
              url: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/HOUR_24.png',
              extent: { xmin: 119.77, ymin: 21.746776427875963, xmax: 122.67, ymax: 25.449655229327627 },
            },
          ],
        },
      ],
      auxiliaryList: [
        {
          id: 'MOI_CONTOUR_2',
          name: i18n.t('map.contour') || '等高線圖',
          type: 'wmts',
          showInMenu: false,
          url: 'https://wmts.nlsc.gov.tw/wmts/MOI_CONTOUR_2/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}',
          visible: false,
          opacity: 0.5 ,
        },
        {
          id: 'shakemap',
          name: '地震震度圖',
          type: 'media',
          showInMenu: false,
          url: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/shakemap.png',
          visible: false, // 控制 UI 顯示狀態
          opacity: 0.5,   // 設定透明度
          extent: {
            xmin: 118.7310766337565, ymin: 20.87166146375304, xmax: 123.1109543958627, ymax: 26.02948317724592,
          },
        },
        {
          id: 'shp_group',
          name: '地質敏感區',
          type: 'shp_group', // 群組標記
          showInMenu: false,
          visible: false,    // 父層開關
          opacity: 0.8,
          activeChildId: 'shp_11', // 預設選中 (例如桃園)
          children: [
            { id: 'shp_11', name: '桃園市', type: 'shp_zip', url: 'https://api-geoserver.geosupply.com.tw/shp//L0011.zip' },
            { id: 'shp_12', name: '新竹縣市', type: 'shp_zip', url: 'https://api-geoserver.geosupply.com.tw/shp//L0012.zip' },
            { id: 'shp_13', name: '苗栗縣', type: 'shp_zip', url: 'https://api-geoserver.geosupply.com.tw/shp//L0013.zip' },
            { id: 'shp_14', name: '彰化縣', type: 'shp_zip', url: 'https://api-geoserver.geosupply.com.tw/shp//L0014.zip' },
          ],
        },
      ],
    };
  },
  computed: {
    // 1. 過濾底圖清單
    filteredBasemapList() {
      return this.basemapList.filter(item => item.showInMenu !== false);
    },

    // 新增台鐵圖層過濾
    filteredTraList() {
      return this.traList.filter(item => item.showInMenu !== false);
    },

    // 2. 過濾氣象圖層清單 (父層)
    filteredOverlayList() {
      return this.overlayList.filter(item => item.showInMenu !== false);
    },

    // 3. 過濾輔助圖層清單 (父層)
    filteredAuxiliaryList() {
      return this.auxiliaryList.filter(item => item.showInMenu !== false);
    },

    // NEW: 判斷是否全選
    isAllInstrumentsChecked() {
      return this.instrumentTypes.length > 0 && this.instrumentTypes.every(t => t.visible);
    },

    showInstrumentMenu() {
      return isThsrProject(this.$store.getters['project/selectedProject']._id);
    },

    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },

    userRole() {
      return this.$store.getters['user/details'].roles;
    },

    badInstrumentStatusList() {
      // 1. 基本過濾：有座標、座標在台灣範圍內(>100)、狀態非 GREEN
      let list = this.rawInstrumentList.filter(inst => 
        inst.coordinates && 
        inst.coordinates[0] > 100 && 
        inst.status !== 'GREEN',
      );

      // 2. 去重複邏輯 (移植自舊版：同一個座標點只顯示一筆)
      const keys = {};
      list = list.filter(inst => {
        // 排除名稱包含 TK66
        if (inst.name.includes('TK66')) return false;
        
        const key = `${inst.coordinates[0]},${inst.coordinates[1]}`;
        if (keys[key]) return false; // 如果該座標已經出現過，則過濾掉
        
        keys[key] = true;
        return true;
      });

      return list;
    },
  },
  async mounted() {
    const [WebMap, Map, MapView, FeatureLayer, Basemap, WebTileLayer, ScaleBar, Locate, MediaLayer, ImageElement, Extent, KMLLayer, GraphicsLayer, Graphic, SpatialReference, esriConfig, urlUtils] = await loadModules(
      [
        'esri/WebMap',
        'esri/Map', 
        'esri/views/MapView', 
        'esri/layers/FeatureLayer',
        'esri/Basemap',
        'esri/layers/WebTileLayer',
        'esri/widgets/ScaleBar',
        'esri/widgets/Locate',
        'esri/layers/MediaLayer',
        'esri/layers/support/ImageElement',
        'esri/geometry/Extent',
        'esri/layers/KMLLayer',
        'esri/layers/GraphicsLayer',
        'esri/Graphic',
        'esri/geometry/SpatialReference',
        'esri/config',
        'esri/core/urlUtils',
      ],
      {
        url: 'https://js.arcgis.com/4.34/',
        css: 'https://js.arcgis.com/4.34/esri/themes/light/main.css',
      },
    );

    // 將類別存入變數供 methods 使用
    BasemapClass = Basemap;
    WebTileLayerClass = WebTileLayer;
    MediaLayerClass = MediaLayer;
    ImageElementClass = ImageElement;
    ExtentClass = Extent;
    KMLLayerClass = KMLLayer;
    GraphicsLayerClass = GraphicsLayer; 
    GraphicClass = Graphic; 
    SpatialReferenceClass = SpatialReference;

    if (isThsrProject(this.selectedProject._id)) {
      esriConfig.request.proxyUrl = 'https://arcgis.thsrc.com.tw/proxy/proxy.ashx';
      urlUtils.addProxyRule({
        urlPrefix: 'arcgis.thsrc.com.tw',
        proxyUrl: 'https://arcgis.thsrc.com.tw/proxy/proxy.ashx',
      });
      
      esriConfig.portalUrl = 'https://arcgis.thsrc.com.tw/portal';
    }

    // 先建立空白 FeatureLayer，資料之後用 API 填入
    this.layer = new FeatureLayer({
      source: [],
      objectIdField: 'oid',
      geometryType: 'point',
      spatialReference: { wkid: 4326 },

      fields: [
        { name: 'oid', type: 'oid' },
        { name: 'instrument_id', type: 'string' },
        { name: 'instrument_name', type: 'string' },  // 用於顯示名稱標籤
        { name: 'popup_title', type: 'string' },      // 彈窗標題
        { name: 'status', type: 'string' },           // 用於決定 Marker 顏色 (最嚴重狀態)
        { name: 'info_html', type: 'string' },        // 存放 Popup 的完整美化 HTML
        { name: 'instrument_type', type: 'string' },  // 代表儀器類型
        { name: 'instrument_types', type: 'string' }, // 所有儀器類型（用於篩選）
      ],

      renderer: {
        type: 'unique-value',
        field: 'status',
        defaultSymbol: this.getMarker('gray'),
        uniqueValueInfos: [
          { value: 'GREEN', symbol: this.getMarker('#67C23A') },
          { value: 'AL', symbol: this.getMarker('#FFAD00') },
          { value: 'WSL', symbol: this.getMarker('#F56C6C') },
          { value: 'down', symbol: this.getMarker('#909399') },
        ],
      },

      labelingInfo: [
        {
          labelPlacement: 'center-right',
          deconflictionStrategy: 'dynamic',
          labelExpressionInfo: { expression: '$feature.instrument_name' },
          symbol: {
            type: 'text',
            color: 'black',
            haloColor: 'white',
            haloSize: 1.5,
            font: {
              size: 10,
              family: 'sans-serif',
            },
          },
        },
      ],

      popupTemplate: {
        title: '{popup_title}',
        content: '{info_html}', 
      },
    });

    this.layer.custom_zIndex = 999;
    // 初始化 Map
    let map = null;
    let initialBasemapId = 'topo-vector';

    if (isThsrProject(this.selectedProject._id) && (this.userRole === 'thsr' || this.userRole === 'admin')) {
      try {
        this.loading = false;
        const webmap = new WebMap({
          portalItem: {
            id: this.webmapId,
          },
        });
        await webmap.load();
        map = webmap;

        // ⭐ 成功載入後，立刻將 WebMap 的原始底圖存入變數
        if (webmap.basemap) {
          this.originalWebMapBasemap = webmap.basemap;
          console.log('WebMap 原始底圖已備份');
        }

        // ⭐ 成功載入 WebMap 專案地圖後，設定 WebMap 模式
        this.isWebMapMode = true;
        initialBasemapId = this.webmapId; // 將初始底圖 ID 設為 WebMap ID
        this.currentBasemapId = this.webmapId;

      } catch (error) {
        this.loading = true;
        console.error('load failed. => use normal map', error);
        map = new Map({ basemap: 'topo-vector' });
        this.isWebMapMode = false;
        this.currentBasemapId = 'topo-vector'; // 使用預設底圖 ID
      }
    } else {
      this.loading = true;
      map = new Map({ basemap: 'topo-vector' });
      this.isWebMapMode = false;
      this.currentBasemapId = 'topo-vector'; // 使用預設底圖 ID
    }

    // **根據初始化結果，調整「預設地圖」選項的 ID**
    const defaultBasemapItem = this.basemapList.find(item => item.id === 'initial-default-map');
    if (defaultBasemapItem) {
      // 如果是 WebMap 模式，將 ID 設為 WebMap ID，這樣點擊時才能觸發 WebMap 邏輯
      defaultBasemapItem.id = this.isWebMapMode ? this.webmapId : 'topo-vector';
    }

    map.add(this.layer);

    // 初始化 MapView
    this.view = new MapView({
      container: 'mapView',
      map,
      center: [120.60622, 24.05705],
      zoom: 8,
      popup: {
        dockOptions: {
          buttonEnabled: false,
        },
      },
    });

    // 等 View 完整載入再開始載入資料
    await this.view.when(() => {
      console.log('%cMapView READY ✔', 'color: green; font-size:14px;');

      this.view.ui.remove('zoom');

      // 加入 Locate (定位) Widget
      var locateBtn = new Locate({
        view: this.view,
        popupEnabled: false,
      });
      this.view.ui.add(locateBtn, { position: 'top-right' });

      // 加入 ScaleBar (比例尺) Widget
      const scaleBar = new ScaleBar({
        view: this.view,
        unit: 'metric', // 設定為公制 (m/km)
        style: 'ruler',  // 樣式: 'ruler' (直尺) 或 'line' (線條)
      });
      this.view.ui.add(scaleBar, { position: 'bottom-right' });
    });

    await this.layer.when(() => {
      console.log('%cFeatureLayer READY ✔', 'color: green; font-size:14px;');
      this.getProject();
    });
  },
  methods: {
    // NEW: 切換展開/收合狀態
    toggleTraExpansion(item) {
      // 這裡只改 expanded，不改 visible，地圖不會閃爍
      item.expanded = !item.expanded;
    },

    // NEW: 全選/取消全選 台鐵圖層
    toggleAllTraGroup(groupItem) {
      // 判斷當前是否「全部已選」
      const allChecked = groupItem.children.every(c => c.visible);
      
      // 如果全部已選，則目標是「全不選」；否則目標是「全選」
      const targetState = !allChecked;

      // 更新父層狀態 (僅供 UI 參考)
      groupItem.visible = targetState;

      // 更新所有子層
      groupItem.children.forEach(child => {
        if (child.visible !== targetState) {
          child.visible = targetState;
          // 觸發地圖圖層開關
          this.handleLayerDisplay(child, targetState);
        }
      });
    },

    // NEW: 判斷父層 Checkbox 是否勾選 (全部子層都勾選才算勾選)
    isTraGroupChecked(item) {
      return item.children && item.children.length > 0 && item.children.every(c => c.visible);
    },

    // NEW: 判斷父層 Checkbox 是否為「部分選中」狀態 (Indeterminate)
    isTraGroupIndeterminate(item) {
      if (!item.children) return false;
      const checkedCount = item.children.filter(c => c.visible).length;
      return checkedCount > 0 && checkedCount < item.children.length;
    },

    // 處理台鐵子圖層開關
    toggleTraChild(parent, child) {
      child.visible = !child.visible;
      // 如果打開，則確保顯示
      if (child.visible) {
        this.handleLayerDisplay(child, true);
      } else {
        this.handleLayerDisplay(child, false);
      }
    },

    // 1. Toggle Layer
    toggleLayer(item) {
      if (!this.view) return;
      item.visible = !item.visible;

      if (item.children) {
        // 群組邏輯 (如雨量)
        const activeChild = item.children.find(c => c.id === item.activeChildId);
        if (activeChild) {
          this.handleLayerDisplay(activeChild, item.visible, item.opacity);
        }
      } else {
        // 單一圖層 (如雷達、颱風、等高線)
        this.handleLayerDisplay(item, item.visible, item.opacity);
      }
    },

    // 2. Switch Child Layer
    switchChildLayer(parentItem, childItem) {
      if (!this.view || !parentItem.visible) return;
      if (parentItem.activeChildId === childItem.id) return;

      const oldChild = parentItem.children.find(c => c.id === parentItem.activeChildId);
      if (oldChild) this.handleLayerDisplay(oldChild, false);

      parentItem.activeChildId = childItem.id;
      this.handleLayerDisplay(childItem, true, parentItem.opacity);
    },

    // 3. 通用顯示處理
    handleLayerDisplay(config, isVisible, opacity = 1) {
      if (this.createdLayers[config.id]) {
        this.createdLayers[config.id].visible = isVisible;
      } else if (isVisible) {
        // 如果是台鐵圖層，呼叫專門的建立邏輯
        if (config.url && (config.url.endsWith('.kml') || config.url.endsWith('.kmz'))) {
          this.createTraLayer(config);
        } else {
          this.createAndAddOverlay({ ...config, opacity });
        }
      }
    },

    // 4. 通用圖層建立工廠 (Factory Pattern)
    async createAndAddOverlay(config) {
      try {
        let newLayer = null;

        // A. 處理 KML (颱風)
        if (config.type === 'kml') {
          newLayer = new KMLLayerClass({
            url: config.url,
            title: config.name,
            opacity: config.opacity || 1,
            visible: true,
          });
        }
        // B. 處理 WMTS/WebTile (等高線)
        else if (config.type === 'wmts') {
          const urlTemplate = config.url
            .replace('{Style}', 'default')
            .replace('{TileMatrixSet}', 'GoogleMapsCompatible')
            .replace('{TileMatrix}', '{level}')
            .replace('{TileRow}', '{row}')
            .replace('{TileCol}', '{col}');
            
          newLayer = new WebTileLayerClass({
            urlTemplate,
            title: config.name,
            copyright: 'NLSC',
            opacity: config.opacity || 1,
            visible: true,
          });
        }
        // C. 處理 MediaLayer (雷達、雨量)
        else if (config.type === 'media') {
          const extent = new ExtentClass({
            xmin: config.extent.xmin, ymin: config.extent.ymin,
            xmax: config.extent.xmax, ymax: config.extent.ymax,
            spatialReference: { wkid: 4326 },
          });
          const element = new ImageElementClass({
            image: config.url,
            georeference: { type: 'extent-and-rotation', extent },
          });
          newLayer = new MediaLayerClass({
            source: [element],
            title: config.name,
            opacity: config.opacity || 0.7,
            visible: true,
          });
        }
        else if (config.type === 'shp_zip') {
          // 呼叫專門處理 SHP 的函式
          newLayer = await this.createGraphicsLayerFromZip(config);
        }

        if (newLayer) {
          this.view.map.add(newLayer);
          this.createdLayers[config.id] = newLayer;
          // 🔥 【新增這段】強制將 instruments layer 移到最上層
          if (this.layer) {
            this.view.map.reorder(this.layer, this.view.map.layers.length - 1);
          }
          console.log(`圖層 [${config.name}] 已建立`);
        }
      } catch (error) {
        console.error(`建立圖層 [${config.name}] 失敗:`, error);
        config.visible = false;
      }
    },

    // NEW: 切換單一儀器類型顯示
    toggleInstrumentType(typeObj) {
      typeObj.visible = !typeObj.visible;
      this.updateLayerDefinition();
    },

    // NEW: 切換全選
    toggleAllInstruments() {
      const targetState = !this.isAllInstrumentsChecked;
      this.instrumentTypes.forEach(t => t.visible = targetState);
      this.updateLayerDefinition();
    },

    // NEW: 更新 FeatureLayer 的過濾條件 (核心邏輯)
    updateLayerDefinition() {
      if (!this.layer) return;

      // 找出所有勾選的類型名稱
      const visibleTypes = this.instrumentTypes
        .filter(t => t.visible)
        .map(t => t.name);

      if (visibleTypes.length === 0) {
        // 如果都沒選，隱藏所有 (1=0 為 False)
        this.layer.definitionExpression = '1=0';
      } else if (visibleTypes.length === this.instrumentTypes.length) {
        // 如果全選，移除過濾條件 (顯示全部)
        this.layer.definitionExpression = null;
      } else {
        // 比對 instrument_types 欄位 (格式例如 ,TM,ELP,)
        const clauses = visibleTypes.map(t => `instrument_types LIKE '%,${t},%'`);
        this.layer.definitionExpression = clauses.join(' OR ');
      }
    },

    // 移植自 MapComponets_TRA 的解析邏輯
    async createTraLayer(config) {
      try {
        const graphics = await this.loadKmlGeometries(config.url);
        if (graphics && graphics.length > 0) {
          const layer = this.createKmlFeatureLayer(graphics, config.color, config.name);
          
          layer.custom_zIndex = config.zIndex || 0;

          this.view.map.add(layer);
          this.createdLayers[config.id] = layer;

          this.reorderLayers();
        }
      } catch (error) {
        console.error(`台鐵圖層 [${config.name}] 載入失敗:`, error);
      }
    },

    reorderLayers() {
      if (!this.view || !this.view.map) return;

      const layers = this.view.map.layers.toArray();

      layers.sort((a, b) => {
        const zA = a.custom_zIndex || 0;
        const zB = b.custom_zIndex || 0;
        return zA - zB;
      });

      layers.forEach((layer, index) => {
        this.view.map.reorder(layer, index);
      });
      
      console.log('圖層已依優先級重新排序');
    },

    createKmlFeatureLayer(graphics, colorHex, title) {
      // 使用傳入的 GraphicClass (mounted 時取得)
      return new this.layer.constructor({ // 借用 FeatureLayer 的構造函數
        source: graphics,
        title,
        objectIdField: 'ObjectID',
        fields: [
          { name: 'ObjectID', type: 'oid' },
          { name: 'name', type: 'string' },
        ],
        renderer: {
          type: 'simple',
          symbol: {
            type: graphics[0]?.geometry.type === 'polyline' ? 'simple-line' : 'simple-marker',
            color: colorHex,
            width: 2,
            size: 6,
            outline: { color: 'white', width: 1 },
          },
        },
        labelingInfo: [{
          labelExpressionInfo: { expression: '$feature.name' },
          symbol: { type: 'text', color: 'black', haloColor: 'white', haloSize: 1, font: { size: 9 } },
        }],
      });
    },

    async loadKmlGeometries(url) {
      let kmlText = '';
      const response = await fetch(url);
      const blob = await response.blob();

      if (url.toLowerCase().endsWith('.kmz')) {
        const zip = await JSZip.loadAsync(blob);
        const kmlFileName = Object.keys(zip.files).find(name => name.endsWith('.kml'));
        if (kmlFileName) kmlText = await zip.file(kmlFileName).async('string');
      } else {
        kmlText = await blob.text();
      }

      const parser = new DOMParser();
      const xml = parser.parseFromString(kmlText, 'application/xml');
      const placemarks = xml.getElementsByTagName('Placemark');
      const graphics = [];
      let oid = 1;

      for (let p of placemarks) {
        const nameNode = p.getElementsByTagName('name')[0];
        const name = nameNode?.textContent || 'NoName';

        // 線型解析
        const lineNode = p.getElementsByTagName('LineString')[0];
        if (lineNode) {
          const coordNode = lineNode.getElementsByTagName('coordinates')[0];
          if (coordNode) {
            const paths = coordNode.textContent.trim().split(/\s+/).map(c => c.split(',').map(Number).slice(0, 2));
            graphics.push(new GraphicClass({
              geometry: { type: 'polyline', paths: [paths] },
              attributes: { ObjectID: oid++, name },
            }));
          }
          continue;
        }
        // 點型解析 (依需求決定是否保留)
        const pointNode = p.getElementsByTagName('Point')[0];
        if (pointNode) {
          const [lon, lat] = pointNode.getElementsByTagName('coordinates')[0].textContent.trim().split(',').map(Number);
          graphics.push(new GraphicClass({
            geometry: { type: 'point', longitude: lon, latitude: lat },
            attributes: { ObjectID: oid++, name },
          }));
        }
      }
      return graphics;
    },

    async createGraphicsLayerFromZip(config) {
      try {
        // 1. 下載 Zip
        const response = await fetch(config.url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const fileBlob = await response.blob();
        const buffer = await fileBlob.arrayBuffer();

        // 2. 解析 Zip (使用 shpjs)
        const geojson = await shp(buffer);
        
        // 3. 轉換為 Graphics
        let graphics = [];
        let symbol;
        // 取得第一個要素的類型來決定整個圖層的 Symbol
        const geometryType = geojson.features?.[0]?.geometry?.type || 'Polygon';

        // 設定 Symbol 樣式
        if (geometryType === 'Point' || geometryType === 'MultiPoint') {
          symbol = {
            type: 'simple-marker',
            color: 'rgba(226, 119, 40, 0.8)',
            outline: { color: 'white', width: 1 },
          };
        } else if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
          symbol = {
            type: 'simple-line',
            color: 'rgba(227, 139, 79, 0.8)',
            width: 3,
          };
        } else { // Polygon
          symbol = {
            type: 'simple-fill',
            color: 'rgba(227, 139, 79, 0.4)',
            outline: { color: 'white', width: 1 },
          };
        }

        // 遍歷並建立 Graphic
        geojson.features.forEach(feature => {
          // 使用 arcgis-to-geojson-utils 轉換
          const arcgisJson = geojsonToArcGIS(feature);
          
          // 判斷並指定 geometry.type
          let type;
          if (arcgisJson.geometry.rings) {
            type = 'polygon';
          } else if (arcgisJson.geometry.paths) {
            type = 'polyline';
          } else if (arcgisJson.geometry.x !== undefined) {
            type = 'point';
          } else if (arcgisJson.geometry.points) {
            type = 'multipoint'; // 雖然 shpjs 轉出來通常是 points，但要注意 JS API 格式
          }

          const graphic = new GraphicClass({
            geometry: {
              type,
              ...arcgisJson.geometry,
              spatialReference: { wkid: 4326 }, // 假設 GeoJSON 是 WGS84
            },
            symbol,
            attributes: arcgisJson.attributes,
          });
          graphics.push(graphic);
        });

        // 4. 建立 GraphicsLayer
        const layer = new GraphicsLayerClass({
          title: config.name,
          graphics,
          opacity: config.opacity || 0.5,
          visible: true,
        });

        return layer;

      } catch (err) {
        console.error('SHP 解析失敗:', err);
        throw err;
      }
    },

    changeBasemap(basemapConfig) {
      if (!this.view) return;
      
      this.currentBasemapId = basemapConfig.id;

      // ⭐ 切換回「預設地圖」且當前是 WebMap 模式
      if (basemapConfig.id === this.webmapId) {
        
        // 檢查是否有備份的原始底圖
        if (this.isWebMapMode && this.originalWebMapBasemap) {
          // 核心解法：將 Map 的底圖屬性，設定回原本備份的物件
          this.view.map.basemap = this.originalWebMapBasemap;
        } else {
          // 如果沒有備份 (或是非 WebMap 模式)，則使用備案 (topo-vector)
          this.view.map.basemap = 'topo-vector';
        }
        return; // 結束函式，不執行後面的邏輯
      }

      // 情況 A: 切換回 ArcGIS 預設向量底圖
      if (basemapConfig.type === 'default') {
        this.view.map.basemap = basemapConfig.id; // 直接設定字串，如 'topo-vector'
        return;
      }

      // 情況 B: 切換至自定義 WMTS (使用 WebTileLayer)
      if (basemapConfig.type === 'wmts') {
        // 轉換 URL 格式：
        // 原始: .../{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}
        // ArcGIS WebTileLayer 需求: .../default/GoogleMapsCompatible/{level}/{row}/{col}
        
        const urlTemplate = basemapConfig.url
          .replace('{Style}', 'default')
          .replace('{TileMatrixSet}', 'GoogleMapsCompatible')
          .replace('{TileMatrix}', '{level}') // ArcGIS 使用 level 代表 zoom/matrix
          .replace('{TileRow}', '{row}')
          .replace('{TileCol}', '{col}');

        // 建立圖層
        const tileLayer = new WebTileLayerClass({
          urlTemplate,
          title: basemapConfig.name,
          copyright: 'NLSC',
        });

        // 建立底圖物件並套用
        const customBasemap = new BasemapClass({
          baseLayers: [tileLayer],
          title: basemapConfig.name,
          id: basemapConfig.id,
        });

        this.view.map.basemap = customBasemap;
      }
    },

    getStatusPriority(status) {
      const s = (status || '').toUpperCase();
      if (s === 'WSL') return 3;   // 紅色 (最嚴重)
      if (s === 'AL') return 2;    // 黃色
      if (s === 'GREEN') return 1; // 綠色
      return 0;                    // 其他/Down
    },

    getMarker(color) {
      return {
        type: 'simple-marker',
        color,
        size: 10,
        outline: {
          color: '#000000', // 細黑色外框
          width: 0.8,
        },
      };
    },

    getStatusBadge(status) {
      const s = (status || '').toUpperCase();
      switch (s) {
        case 'WSL':
          return '<span style="display:inline-block; padding:1px 5px; border-radius:3px; font-size:11px; font-weight:600; background:#fef0f0; color:#f56c6c; border:1px solid #fde2e2; line-height:1.2;">WSL</span>';
        case 'AL':
          return '<span style="display:inline-block; padding:1px 5px; border-radius:3px; font-size:11px; font-weight:600; background:#fdf6ec; color:#e6a23c; border:1px solid #faecd8; line-height:1.2;">AL</span>';
        case 'GREEN':
          return '<span style="display:inline-block; padding:1px 5px; border-radius:3px; font-size:11px; font-weight:600; background:#f0f9eb; color:#67c23a; border:1px solid #e1f3d8; line-height:1.2;">正常</span>';
        default:
          return '<span style="display:inline-block; padding:1px 5px; border-radius:3px; font-size:11px; font-weight:600; background:#f4f4f5; color:#909399; border:1px solid #e9e9eb; line-height:1.2;">離線</span>';
      }
    },

    generateStationPopupHtml(arg1, arg2) {
      const instruments = Array.isArray(arg1) ? arg1 : (Array.isArray(arg2) ? arg2 : []);
      let rowsHtml = '';

      instruments.forEach((inst, idx) => {
        const badge = this.getStatusBadge(inst.status);
        const readingVal = (inst.reading !== null && inst.reading !== undefined && inst.reading !== '')
          ? inst.reading
          : '—';
        const rawTime = inst.TIMESTAMP || inst.timestamp;
        let timeVal = '—';
        if (rawTime) {
          const m = moment(rawTime);
          timeVal = m.isValid() ? m.utcOffset('+08:00').format('YYYY-MM-DD HH:mm:ss') : rawTime;
        }
        const bg = idx % 2 === 0 ? '#ffffff' : '#fafafa';

        rowsHtml += `<tr style="background:${bg}; border-bottom:1px solid #ebeef5;"><td style="padding:4px 6px; font-weight:600; color:#303133; font-size:12px; white-space:nowrap; text-align:left;">${inst.name}</td><td style="padding:4px 6px; text-align:left; font-weight:600; color:#1f2f3d; font-size:12px; white-space:nowrap;">${readingVal}</td><td style="padding:4px 6px; text-align:left; white-space:nowrap;">${badge}</td><td style="padding:4px 6px; text-align:left; font-size:11px; color:#8c939d; font-family:monospace; white-space:nowrap;">${timeVal}</td></tr>`;
      });

      const tableHtml = `<div style="font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Microsoft JhengHei', sans-serif; font-size:12px; color:#333; line-height:1.35; padding:0; margin:0;"><table style="width:100%; border-collapse:collapse; text-align:left;"><thead><tr style="background:#f5f7fa; color:#606266; font-size:11px; border-bottom:1px solid #e4e7ed;"><th style="padding:4px 6px; text-align:left; font-weight:600;">儀器名稱</th><th style="padding:4px 6px; text-align:left; font-weight:600;">讀值</th><th style="padding:4px 6px; text-align:left; font-weight:600;">狀態</th><th style="padding:4px 6px; text-align:left; font-weight:600;">時間</th></tr></thead><tbody>${rowsHtml}</tbody></table></div>`;

      return tableHtml.replace(/[\r\n]+/g, '').trim();
    },

    // NEW: 取得狀態顏色
    getStatusColor(status) {
      switch (status) {
        case 'WSL': return '#F56C6C'; // 紅色
        case 'AL': return '#FFAD00';  // 黃色
        case 'WL': return '#4188FF';  // 藍色
        default: return '#bbb';       // 灰色
      }
    },

    // NEW: 移動地圖視角並開啟對應儀器 Popup
    async flyToInstrument(inst) {
      if (!this.view) return;

      const targetDetails = {
        target: [inst.coordinates[0], inst.coordinates[1]],
        zoom: 18, // 設定縮放層級
      };

      try {
        await this.view.goTo(targetDetails);
        if (this.layer && inst.coordinates) {
          const query = this.layer.createQuery();
          query.geometry = {
            type: 'point',
            longitude: inst.coordinates[0],
            latitude: inst.coordinates[1],
          };
          query.distance = 20; // 20 米公差
          query.units = 'meters';
          query.returnGeometry = true;
          const result = await this.layer.queryFeatures(query);
          if (result && result.features && result.features.length > 0) {
            const feat = result.features[0];
            this.view.popup.open({
              features: [feat],
              location: feat.geometry,
            });
          }
        }
      } catch (error) {
        if (error && error.name !== 'AbortError') {
          console.error(error);
        }
      }
    },
    
    async getProject() {
      // 步驟 1: 取得資料
      let res;
      try {
        res = await this.$axios({
          category: 'project',
          method: 'get',
          apiName: `${this.selectedProject._id}/station-instrument`,
        });
      } catch (e) {
        console.error(e);
        this.loading = false;
        return;
      }

      // 根據專案允許的圖層，決定哪些圖層要顯示在選單中
      this.selectedProject.allowed_layer.forEach(lyr => {
        if (this.allowLayer[lyr]) {
          const targetOverlay = this.overlayList.find(item => item.id === this.allowLayer[lyr]);
          if (targetOverlay) targetOverlay.showInMenu = true;

          const targetAuxiliary = this.auxiliaryList.find(item => item.id === this.allowLayer[lyr]);
          if (targetAuxiliary) targetAuxiliary.showInMenu = true;

          // 台鐵圖層處理
          const targetTra = this.traList.find(g => g.id === this.allowLayer[lyr]);
          if (targetTra) {
            targetTra.showInMenu = true;
            // 核心：如果父層與子層預設都是開啟的，立即執行 handleLayerDisplay
            if (targetTra.visible) {
              targetTra.children.forEach(child => {
                if (child.visible) {
                  this.handleLayerDisplay(child, true);
                }
              });
            }
          }
        }
      });

      // --- 資料分組邏輯 ---
      
      // 用來依座標分組的 Map
      // Key: "經度,緯度" 字串, Value: 該位置的所有儀器資料陣列
      const locationMap = new Map();
      // NEW: 用來計算類型數量的暫存物件
      const typeCounts = {};
      // --- RESET LIST ---
      this.rawInstrumentList = []; // 重置清單

      res.data.forEach((station) => {
        station.instrument.forEach((item) => {
          const coords = item.coordinates;
          // 驗證座標
          const valid =
            Array.isArray(coords) &&
            coords.length === 2 &&
            coords.every(c => c !== null && c !== undefined && !isNaN(c));

          if (!valid) return;

          // NEW: 將資料推入 rawInstrumentList 供列表使用
          this.rawInstrumentList.push({
            ...item,
            _stationName: station.name,
          });

          // NEW: 解析儀器類型
          let typeName = 'Other';
          if (item.name && item.name.includes('-')) {
            typeName = item.name.split('-')[1];
          }
          
          // 累加計數
          if (!typeCounts[typeName]) typeCounts[typeName] = 0;
          typeCounts[typeName]++;
          
          // 建立唯一 Key (使用經緯度組合)
          const locKey = `${coords[0]},${coords[1]}`;

          if (!locationMap.has(locKey)) {
            locationMap.set(locKey, []);
          }
          // 將 typeName 也塞入 item 物件中方便稍後取用
          item._parsedType = typeName; 
          locationMap.get(locKey).push(item);
        });
      });

      typeCounts['STI'] /= 2; // STI 類型數量除以 2 (因為每個 STI 有兩個點)

      // NEW: 生成選單資料
      this.instrumentTypes = Object.keys(typeCounts).sort().map(name => ({
        name,
        visible: true, // 預設開啟
        count: typeCounts[name],
      }));
      const newFeatures = [];
      let featureOidCounter = 1;

      // 遍歷分組後的資料，為每個經緯度座標建立單一 Feature（彙整同座標所有儀器）
      locationMap.forEach((instruments, locKey) => {
        const firstItem = instruments[0];
        const stationName = firstItem._stationName || '';

        // 1. 決定最嚴重狀態 (WSL > AL > GREEN > down)
        let worstStatus = 'down';
        let maxPriority = -1;
        const typesSet = new Set();
        const nameLabels = [];

        instruments.forEach(inst => {
          const currentStatus = (inst.status || 'down').toUpperCase();
          const currentPriority = this.getStatusPriority(currentStatus);

          if (currentPriority > maxPriority) {
            maxPriority = currentPriority;
            worstStatus = ['WSL', 'AL', 'GREEN'].includes(currentStatus) ? currentStatus : 'down';
          }

          if (inst._parsedType) typesSet.add(inst._parsedType);
          nameLabels.push(inst.name);
        });

        // 2. 彈窗標題與地圖標籤文字
        const popupTitle = instruments.length > 1
          ? `${stationName || nameLabels[0]} (共 ${instruments.length} 台儀器)`
          : nameLabels[0];

        const mapLabel = instruments.length > 1
          ? (stationName || nameLabels[0])
          : nameLabels[0];

        // 3. 生成美化的 Popup 內容
        const popupHtml = this.generateStationPopupHtml(instruments);

        // 4. 儀器類型集合，格式為 ,TM,ELP, 以便 SQL LIKE 查詢
        const typesStr = `,${Array.from(typesSet).join(',')},`;

        newFeatures.push({
          geometry: {
            type: 'point',
            longitude: firstItem.coordinates[0],
            latitude: firstItem.coordinates[1],
          },
          attributes: {
            oid: featureOidCounter++,
            instrument_id: firstItem._id,
            instrument_name: mapLabel,
            popup_title: popupTitle,
            status: worstStatus,
            info_html: popupHtml,
            instrument_type: firstItem._parsedType || 'Other',
            instrument_types: typesStr,
          },
        });
      });

      // 步驟 2: 使用 applyEdits 更新圖層 (保持您之前的修正)
      if (this.layer && this.layer.loaded) {
        try {
          const queryResults = await this.layer.queryFeatures({
            where: '1=1',
            returnIdsOnly: true,
          });

          const existingOids = queryResults && queryResults.objectIds ? queryResults.objectIds : [];
          const deleteOids = existingOids.map(oid => ({ objectId: oid }));

          await this.layer.applyEdits({
            deleteFeatures: deleteOids,
            addFeatures: newFeatures,
          });

          // NEW: 資料載入後，重置過濾條件確保顯示正確
          this.updateLayerDefinition();

          console.log('%cFeatureLayer UPDATED ✔', 'color: green; font-size:14px;');

          if (newFeatures.length > 0) {
            const extentQuery = await this.layer.queryExtent({ where: '1=1' });
            if (extentQuery.extent) {
              this.view.goTo(extentQuery.extent);
            }
          }

        } catch (editError) {
          console.error('FeatureLayer applyEdits 發生錯誤:', editError);
        }
      } else {
        console.warn('FeatureLayer 尚未載入，無法更新資料。');
      }

      this.loading = false;
    },
  },
};
</script>

<style scoped>
/* 確保地圖容器正確 */
#mapView {
  width: 100%;
  height: 100%;
  z-index: 0;
}

/* --- 主選單容器 --- */
/* MODIFY: 新增一個容器來管理所有左上角的控制面板 */
.controls-wrapper {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 99;
  display: flex;       /* 讓子元素並排 */
  flex-direction: row; /* 水平排列 */
  gap: 15px;           /* 兩個選單之間的間距 */
  align-items: flex-start; /* 靠上對齊 */
  pointer-events: none; /* 讓容器不擋住地圖操作，但子元素要開啟 pointer-events */
}

/* MODIFY: 通用的控制面板樣式 */
.control-panel {
  width: 260px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  overflow: hidden;
  transition: width 0.3s ease, height 0.3s ease;
  display: flex;
  flex-direction: column;
  pointer-events: auto; /* 恢復滑鼠互動 */
}

/* 收折狀態 - 兩個選單通用 */
.control-panel.menu-collapsed {
  width: 160px; /* 這裡可以調整收合後的寬度 */
  height: auto;
}

/* 面板內容 */
.panel-content {
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  transition: all 0.3s;
}

/* NEW: 數量標記的樣式 */
.count-badge {
  background-color: #eee;
  color: #777;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: bold;
}

/* --- 標題列 --- */
.panel-header {
  padding: 12px 16px;
  background: #2c3e50; /* 深色標題 */
  color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
}

.panel-title {
  font-weight: 600;
  font-size: 15px;
}

.toggle-icon {
  font-size: 12px;
  color: #bdc3c7;
}

/* --- 內容區 --- */
.panel-content {
  max-height: calc(100vh - 100px); /* 防止超出螢幕 */
  overflow-y: auto; /* 內容過多時捲動 */
}

/* 群組標題 */
.group-title {
  padding: 12px 16px 8px;
  font-size: 12px;
  text-transform: uppercase;
  color: #95a5a6;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.divider {
  height: 1px;
  background: #eee;
  margin: 4px 0;
}

/* --- 選單項目 (通用) --- */
.menu-item {
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;
  color: #34495e;
  font-size: 14px;
}

.menu-item:hover {
  background-color: #f1f4f6;
}

.item-text {
  flex: 1;
}

/* 底圖選中狀態 */
.item-selected {
  background-color: #e8f4fd;
  color: #1890ff;
  font-weight: 500;
  border-right: 3px solid #1890ff;
}

.check-mark {
  color: #1890ff;
  font-weight: bold;
}

/* Checkbox 樣式優化 */
.checkbox-wrapper {
  margin-right: 10px;
  display: flex;
  align-items: center;
}
.checkbox-wrapper input {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

/* --- 子選單容器 (Radio Group) --- */
.child-layer-container {
  background-color: #f8f9fa;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
  padding: 4px 0;
}

.child-item {
  padding: 8px 16px 8px 36px; /* 縮排 */
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #555;
  transition: all 0.2s;
}

.child-item:hover {
  background-color: #e6f7ff;
  color: #1890ff;
}

/* 子項目選中 */
.child-selected {
  color: #1890ff;
  font-weight: 500;
}

/* 自製 Radio Button (純 CSS 繪製，比預設的好看) */
.radio-wrapper {
  margin-right: 10px;
  display: flex;
  align-items: center;
}

.radio-outer {
  width: 14px;
  height: 14px;
  border: 1px solid #ccc;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s;
}

.child-selected .radio-outer {
  border-color: #1890ff;
}

.radio-inner {
  width: 8px;
  height: 8px;
  background: #1890ff;
  border-radius: 50%;
}

/* Vue 過場動畫 (Slide Fade) */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  opacity: 1;
  overflow: hidden;
}
.slide-fade-enter, .slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* 捲軸美化 (Webkit browsers) */
.panel-content::-webkit-scrollbar {
  width: 6px;
}
.panel-content::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}
.panel-content::-webkit-scrollbar-track {
  background-color: transparent;
}

/* 台鐵圖層專用的 Checkbox 對齊 */
.child-item .checkbox-wrapper.child-check {
  margin-right: 10px;
  display: flex;
  align-items: center;
}

/* 顏色標示條 (顯示 KML 線條顏色) */
.color-legend {
  width: 14px;
  height: 4px;
  border-radius: 2px;
  margin-left: auto; /* 推到最右邊 */
  opacity: 0.8;
  box-shadow: 0 0 2px rgba(0,0,0,0.1);
}

/* 確保子項目文字不會擠壓到顏色標示條 */
.child-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
}

/* 父層容器：讓內容可以分邊對齊 */
.tra-header {
  display: flex;
  align-items: center;
  padding: 0; /* 清除原本 padding，改由內部元素控制 */
  cursor: default; /* 父層本身不作為游標熱區，改由內部控制 */
}

/* Checkbox 區域 */
.tra-header .checkbox-wrapper {
  padding: 10px 0 10px 16px; /* 恢復左側間距 */
  cursor: pointer;
}

/* 文字與箭頭區域 (可點擊展開) */
.item-content-clickable {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 10px 10px;
  cursor: pointer;
  height: 100%;
}
.item-content-clickable:hover {
  background-color: rgba(0,0,0,0.02); /* 輕微 Hover 效果 */
}

/* 箭頭動畫 */
.expand-arrow {
  font-size: 10px;
  color: #999;
  transition: transform 0.3s ease;
  transform: rotate(-90deg); /* 預設收起時箭頭向左或向右 */
}
.expand-arrow.is-expanded {
  transform: rotate(0deg); /* 展開時箭頭向下 */
}

/* --- 解決 ScrollBar 問題 --- */
/* 給子選單一個最大高度，超過時出現捲軸 */
.scrollable-list {
  max-height: 250px; /* 您可以依需求調整這個高度 */
  overflow-y: auto;
  overflow-x: hidden;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

/* 美化捲軸 (Chrome/Safari) */
.scrollable-list::-webkit-scrollbar {
  width: 4px;
}
.scrollable-list::-webkit-scrollbar-thumb {
  background-color: #dcdcdc;
  border-radius: 2px;
}
.scrollable-list::-webkit-scrollbar-track {
  background-color: #f5f5f5;
}

/* --- 左下角懸浮容器 --- */
.status-widget-container {
  position: absolute;
  bottom: 30px; /* 距離底部 */
  left: 20px;   /* 距離左側 */
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* 靠左對齊 */
  gap: 10px;
  font-family: 'Segoe UI', sans-serif;
}

/* --- 膠囊按鈕 (常駐顯示) --- */
.status-pill-btn {
  background: white;
  padding: 8px 16px;
  border-radius: 24px; /* 圓角膠囊狀 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.05);
}

.status-pill-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.status-pill-btn.is-active {
  background-color: #f0f2f5; /* 展開時稍微變灰 */
}

.pill-text {
  font-size: 14px;
  color: #555;
  font-weight: 600;
}

.pill-badge {
  background-color: white;
  color: #555;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 8px;
  font-weight: bold;
}

/* --- 展開的列表面板 (Popover) --- */
.status-list-panel {
  width: 240px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  margin-bottom: 5px; /* 與按鈕的間距 */
  border: 1px solid rgba(0,0,0,0.05);
}

.status-header {
  background: #f8f9fa;
  padding: 10px 16px;
  font-size: 13px;
  color: #888;
  font-weight: bold;
  border-bottom: 1px solid #eee;
}

.status-content {
  max-height: 250px; /* 限制高度，超過捲動 */
  overflow-y: auto;
}

/* --- 列表單行樣式 --- */
.status-row {
  padding: 10px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.2s;
}

.status-row:hover {
  background-color: #fff7e6; /* Hover 時淡橘色背景 */
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
}

.inst-name {
  flex: 1;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inst-tag {
  font-size: 10px;
  padding: 1px 4px;
  border: 1px solid;
  border-radius: 3px;
  background-color: white;
  font-weight: bold;
}

/* --- 由下往上的動畫 (Vue Transition) --- */
.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  transform-origin: bottom center;
  opacity: 1;
  transform: translateY(0) scale(1);
}

.slide-up-enter, .slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95); /* 從下方 20px 處淡入 */
}

/* 捲軸美化 */
.status-content::-webkit-scrollbar {
  width: 4px;
}
.status-content::-webkit-scrollbar-thumb {
  background-color: #ddd;
  border-radius: 2px;
}

/* --- 隱藏 ArcGIS Popup 內建 dock 按鈕 --- */
::v-deep .esri-popup__button--dock {
  display: none !important;
}

/* --- 緊湊化 ArcGIS Popup 內外邊距，提升資訊密度 --- */
::v-deep .esri-popup__main-container {
  min-width: 320px !important;
  max-width: 460px !important;
}

::v-deep .esri-popup__header {
  padding: 6px 12px 2px 12px !important;
}

::v-deep .esri-popup__header-title {
  font-size: 13px !important;
  font-weight: 700 !important;
}

::v-deep .esri-popup__content {
  margin: 0 !important;
  padding: 4px 10px 8px 10px !important;
}

/* 徹底隱藏任何由 ArcGIS 自動產生的換行符號避免排版撐開 */
::v-deep br.esri-text-new-line {
  display: none !important;
}
</style>